import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahCareRecipients } from '~constants/api';

import { Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import {
  CareRecipientIgnoreResult,
  CareRecipientLinkResult,
  CareRecipientListQuery,
  CareRecipientListResponse,
  CareRecipientSyncResult,
  CareRecipientUnlinkResult,
  LinkableClient,
} from '~support-at-home/models/careRecipients';

interface LinkCareRecipientArgs {
  care_recipient_id: string;
  client_id: number;
  confirm_overwrite?: boolean;
}

interface CareRecipientIdArgs {
  care_recipient_id: string;
}

const LIST_TAG = { type: 'careRecipients' as const, id: 'LIST' };

export const careRecipientsApi = createApi({
  reducerPath: 'sahCareRecipientsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahCareRecipients }),
  tagTypes: ['careRecipients'],
  endpoints: (builder) => ({
    getCareRecipients: builder.query<CareRecipientListResponse, CareRecipientListQuery & Request>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientListResponse>(payload),
    }),
    // Pulls the latest care recipients from Services Australia into the TurnPoint snapshot the list reads.
    syncCareRecipients: builder.mutation<CareRecipientSyncResult, void>({
      query: () => ({ url: '/sync', method: 'POST' }),
      invalidatesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientSyncResult>(payload),
    }),
    getLinkableClients: builder.query<LinkableClient[], void>({
      query: () => ({ url: '/clients', method: 'GET' }),
      transformResponse: (payload) => unwrap<LinkableClient[]>(payload),
    }),
    linkCareRecipient: builder.mutation<CareRecipientLinkResult, LinkCareRecipientArgs>({
      query: ({ care_recipient_id, client_id, confirm_overwrite }) => ({
        url: `/${encodeURIComponent(care_recipient_id)}/match`,
        method: 'POST',
        body: confirm_overwrite ? { client_id, confirm_overwrite: true } : { client_id },
      }),
      invalidatesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientLinkResult>(payload),
    }),
    unlinkCareRecipient: builder.mutation<CareRecipientUnlinkResult, CareRecipientIdArgs>({
      query: ({ care_recipient_id }) => ({
        url: `/${encodeURIComponent(care_recipient_id)}/match`,
        method: 'DELETE',
      }),
      invalidatesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientUnlinkResult>(payload),
    }),
    ignoreCareRecipient: builder.mutation<CareRecipientIgnoreResult, CareRecipientIdArgs>({
      query: ({ care_recipient_id }) => ({
        url: `/${encodeURIComponent(care_recipient_id)}/ignore`,
        method: 'POST',
      }),
      invalidatesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientIgnoreResult>(payload),
    }),
    restoreCareRecipient: builder.mutation<CareRecipientIgnoreResult, CareRecipientIdArgs>({
      query: ({ care_recipient_id }) => ({
        url: `/${encodeURIComponent(care_recipient_id)}/restore`,
        method: 'POST',
      }),
      invalidatesTags: [LIST_TAG],
      transformResponse: (payload) => unwrap<CareRecipientIgnoreResult>(payload),
    }),
  }),
});

export const {
  useGetCareRecipientsQuery,
  useSyncCareRecipientsMutation,
  useGetLinkableClientsQuery,
  useLinkCareRecipientMutation,
  useUnlinkCareRecipientMutation,
  useIgnoreCareRecipientMutation,
  useRestoreCareRecipientMutation,
} = careRecipientsApi;
